import { Button } from "@components/buttons/button";
import { LandingHeader } from "./LandingHeader";
import {
  FaArrowRight,
  FaBell,
  FaChartLine,
  FaPlay,
  FaShieldAlt,
  FaTicketAlt,
  FaUserFriends,
} from "react-icons/fa";

type LandingHeroProps = {
  isAuthenticated: boolean;
  onGoHome: () => void;
  onGoLogin: () => void;
  onGoEntry: () => void;
};

const heroMetrics = [
  {
    icon: FaChartLine,
    title: "100%",
    text: "online e seguro",
    color: "success",
  },
  {
    icon: FaUserFriends,
    title: "Comissão",
    text: "automática",
    color: "primary",
  },
  {
    icon: FaShieldAlt,
    title: "Transparência",
    text: "nos resultados",
    color: "success",
  },
];

const dashboardMetrics = [
  { label: "Tickets vendidos", value: "7.240", trend: "+12%", color: "dark" },
  { label: "Lucro estimado", value: "R$ 42.800", trend: "+18%", color: "success" },
  { label: "Parceiros ativos", value: "18", trend: "+50%", color: "dark" },
  { label: "Comissão média", value: "12%", trend: "", color: "primary" },
];

const partnerHighlights = [
  { name: "@influencermax", tickets: "1.240 tickets", value: "R$ 1.840" },
  { name: "@perfilvendas", tickets: "980 tickets", value: "R$ 920" },
  { name: "@amigosdobem", tickets: "760 tickets", value: "R$ 510" },
];

export function LandingHero({
  isAuthenticated,
  onGoHome,
  onGoLogin,
  onGoEntry,
}: LandingHeroProps) {
  return (
    <section
      className="landing-hero overflow-hidden border-bottom"
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 78% 18%, rgba(13,110,253,.18), transparent 34%), linear-gradient(135deg, #f8fbff 0%, #eef7ff 48%, #ffffff 100%)",
      }}
    >
      <LandingHeader
        isAuthenticated={isAuthenticated}
        onGoHome={onGoHome}
        onGoLogin={onGoLogin}
        onGoEntry={onGoEntry}
      />

      <div className="landing-hero-content container px-3 px-sm-4 py-4 py-lg-5 d-flex flex-column" style={{ minHeight: "calc(100vh - 96px)" }}>
        <div className="landing-hero-row row align-items-center g-4 g-lg-5 flex-grow-1 py-3 py-lg-5">
          <div className="landing-hero-copy col-lg-5">
            <div className="d-flex flex-column gap-4">
              <div className="d-inline-flex align-items-center gap-2 align-self-start bg-primary text-white rounded-pill px-3 py-2 fw-semibold small shadow-sm">
                <FaTicketAlt />
                Plataforma completa
              </div>

              <div>
                <h1
                  className="fw-bold text-dark mb-3"
                  style={{ fontSize: "clamp(2rem, 8vw, 4rem)", lineHeight: 1.08, maxWidth: 620 }}
                >
                  Campanhas digitais que geram mais vendas, com parceiros e <span className="text-primary">resultados reais.</span>
                </h1>
                <p
                  className="text-muted mb-0"
                  style={{ fontSize: "clamp(1rem, 3.7vw, 1.25rem)", lineHeight: 1.75, maxWidth: 560 }}
                >
                  Crie campanhas, convide afiliados e influenciadores, controle tickets, taxas, comissões e acompanhe o resultado financeiro de forma simples e transparente.
                </p>
              </div>

              <div className="d-grid d-sm-flex gap-3">
                <Button className="btn btn-primary rounded-4 text-white px-4 py-3 shadow-sm" onClick={onGoEntry}>
                  Começar agora <FaArrowRight className="ms-2" />
                </Button>
                <button type="button" className="btn btn-outline-primary rounded-4 px-4 py-3 fw-semibold" onClick={() => document.getElementById("como-funciona")?.scrollIntoView({ behavior: "smooth" })}>
                  <FaPlay className="me-2" />
                  Ver como funciona
                </button>
              </div>

              <div className="landing-hero-metrics row g-3 pt-1 pt-lg-2">
                {heroMetrics.map(({ icon: Icon, title, text, color }) => (
                  <div className="col-4 col-sm-4" key={title}>
                    <div className="d-flex gap-3 align-items-center h-100">
                      <div className={`bg-${color} text-white rounded-3 p-2 d-flex flex-shrink-0`}>
                        <Icon className="fs-4" />
                      </div>
                      <div>
                        <div className="fw-bold text-dark">{title}</div>
                        <small className="text-muted">{text}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="landing-hero-dashboard col-lg-7">
            <div className="bg-white border rounded-4 shadow-lg p-3 p-xl-4">
              <div className="d-flex justify-content-between align-items-center gap-3 mb-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-primary text-white rounded-4 p-3 d-flex">
                    <FaChartLine className="fs-4" />
                  </div>
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <h5 className="fw-bold text-dark mb-0">Campanha Premium</h5>
                      <span className="badge bg-success text-white px-2 py-1">Ativa</span>
                    </div>
                    <small className="text-muted">Visão operacional de vendas, parceiros e resultado</small>
                  </div>
                </div>
                <div className="d-none d-sm-flex align-items-center gap-3">
                  <FaBell className="text-muted" />
                  <button type="button" className="btn btn-primary btn-sm rounded-3" disabled>Compartilhar</button>
                </div>
              </div>

              <div className="row g-3 mb-4">
                {dashboardMetrics.map(({ label, value, trend, color }) => (
                  <div className="col-6 col-xl-3" key={label}>
                    <div className="bg-light rounded-4 p-3 h-100">
                      <small className="text-muted d-block mb-2">{label}</small>
                      <div className={`fs-5 fw-bold text-${color}`}>{value}</div>
                      {trend && <small className="text-success fw-semibold">{trend}</small>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <small className="text-muted">Progresso da campanha</small>
                  <small className="fw-bold text-dark">72%</small>
                </div>
                <div className="progress" style={{ height: 10 }}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated bg-primary" style={{ width: "72%" }} title="Percentual vendido da campanha."></div>
                </div>
                <small className="text-muted">7.240 de 10.000 tickets vendidos</small>
              </div>

              <div className="row g-3">
                <div className="col-xl-7">
                  <div className="border rounded-4 p-3 h-100">
                    <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                      <div className="fw-bold text-dark">Parceiros em Destaque</div>
                      <span className="badge bg-primary text-white px-3 py-2">Comissão Direta</span>
                    </div>
                    {partnerHighlights.map(({ name, tickets, value }) => (
                      <div className="d-flex justify-content-between align-items-center gap-3 mb-2" key={name}>
                        <div>
                          <div className="fw-semibold text-dark">{name}</div>
                          <small className="text-muted">{tickets}</small>
                        </div>
                        <span className="text-success fw-bold text-nowrap">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-xl-5">
                  <div className="border rounded-4 p-3 h-100">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="fw-bold text-dark">Prévia pública</div>
                      <FaTicketAlt className="text-primary" />
                    </div>
                    <div className="bg-light rounded-4 p-3 text-center mb-3">
                      <FaTicketAlt className="fs-2 text-primary mb-2" />
                      <div className="fw-bold text-dark">72% vendido</div>
                    </div>
                    <div className="progress mb-3" style={{ height: 8 }}>
                      <div className="progress-bar bg-primary" style={{ width: "72%" }}></div>
                    </div>
                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <small className="text-muted">Tickets</small>
                        <div className="fw-bold text-dark">7.240</div>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Lucro</small>
                        <div className="fw-bold text-success">R$ 42k</div>
                      </div>
                    </div>
                    <button type="button" className="btn btn-primary btn-sm w-100 rounded-3" disabled>Participar</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
